import { FC, useMemo } from 'react';

import { useFragmentContext } from './FragmentContext';
import { ReqContext } from './FragmentHandler';
import { useSsrEffect } from './useSsrEffect';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataProviderFC<Props = any, Return = any> = FC<Props> & {
  identifier: string;
  initDefaultData: (p: Props, context?: ReqContext) => Promise<Return>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useInitialData<Props extends any = any, Return = any>(
  component: DataProviderFC<Props, Return>,
  params: Props = null,
): Return | null {
  const identifier = component.identifier; // TODO: find automatic way of setting this
  const { dataHandler, fragmentData } = useFragmentContext();

  const data = useMemo(() => fragmentData?.[identifier] || null, [fragmentData, identifier]);

  useSsrEffect(() => {
    if (!data && dataHandler && typeof component.initDefaultData === 'function') {
      dataHandler.createInitDefaultDataPromise(identifier, component.initDefaultData, params);
    }
  });

  return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useSSR<Props = any, Return = any>(
  fn: DataProviderFC<Props, Return>['initDefaultData'],
  identifier: string,
): Return | null {
  const { dataHandler, fragmentData } = useFragmentContext();

  const data = useMemo(() => fragmentData?.[identifier] || null, [fragmentData, identifier]);
  useSsrEffect(() => {
    if (!data && dataHandler && typeof fn === 'function') {
      dataHandler.createInitDefaultDataPromise(identifier, fn, null);
    }
  });

  return data;
}

export { useSSR as experimental_useSSR };
